import { useState, useEffect, useCallback, useRef } from 'react'

/**
 * Custom hook để xử lý responsive canvas
 * @param {Object} containerRef - Ref của container element
 * @param {number} debounceDelay - Delay time cho debounce (ms)
 * @returns {Object} - { width, height, isResizing }
 */
export const useResponsiveCanvas = (containerRef, debounceDelay = 300) => {
    const [dimensions, setDimensions] = useState({
        width: window.innerWidth * 0.7,
        height: window.innerHeight * 0.7
    })
    const [isResizing, setIsResizing] = useState(false)
    const resizeTimeoutRef = useRef(null)
    const resizeStartTimeoutRef = useRef(null)

    const updateDimensions = useCallback(() => {
        if (containerRef?.current) {
            const rect = containerRef.current.getBoundingClientRect()
            // Trừ đi padding và margin nếu có
            const computedStyle = window.getComputedStyle(containerRef.current)
            const paddingX = parseFloat(computedStyle.paddingLeft) + parseFloat(computedStyle.paddingRight)
            const paddingY = parseFloat(computedStyle.paddingTop) + parseFloat(computedStyle.paddingBottom)
            
            const newWidth = rect.width - paddingX || window.innerWidth * 0.7
            const newHeight = rect.height - paddingY || window.innerHeight * 0.7
            
            setDimensions({
                width: Math.floor(newWidth),
                height: Math.floor(newHeight)
            })
        }
    }, [containerRef])

    useEffect(() => {
        const handleResize = () => {
            // Set resizing state
            setIsResizing(true)
            
            // Clear existing timeout
            if (resizeStartTimeoutRef.current) {
                clearTimeout(resizeStartTimeoutRef.current)
            }
            
            // Clear debounce timeout
            if (resizeTimeoutRef.current) {
                clearTimeout(resizeTimeoutRef.current)
            }
            
            // Debounce resize calculation
            resizeTimeoutRef.current = setTimeout(() => {
                updateDimensions()
                setIsResizing(false)
            }, debounceDelay)
        }

        // Initial dimensions
        updateDimensions()

        // Add resize observer nếu browser support
        let resizeObserver = null
        if (containerRef?.current && window.ResizeObserver) {
            resizeObserver = new ResizeObserver(handleResize)
            resizeObserver.observe(containerRef.current)
        }

        // Fallback to window resize event
        window.addEventListener('resize', handleResize)

        return () => {
            window.removeEventListener('resize', handleResize)
            if (resizeObserver && containerRef?.current) {
                resizeObserver.unobserve(containerRef.current)
                resizeObserver.disconnect()
            }
            if (resizeTimeoutRef.current) {
                clearTimeout(resizeTimeoutRef.current)
            }
            if (resizeStartTimeoutRef.current) {
                clearTimeout(resizeStartTimeoutRef.current)
            }
        }
    }, [containerRef, debounceDelay, updateDimensions])

    return {
        width: dimensions.width,
        height: dimensions.height,
        isResizing
    }
}

export default useResponsiveCanvas